import { useMemo } from "react";

import { useQuery } from "@apollo/client";
import camelCase from "lodash/camelCase";
import remove from "lodash/remove";

import {
    getEditableEventFields as GET_EDITABLE_FIELDS,
    getEventFields as GET_EVENT_FIELDS,
    type EventSchemaListReturnType,
    type EditableEventSchemaReturnType,
} from "ServerData/EventSchema/queries";
import { convertTopLevelAttribute } from "Services/HelpersService";
import { EventSchema } from "Types/ContactAttribute";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

function contactAttributesFilter(d: Record<string, any>): boolean {
    /*
        Filter internal Regal system fields from the raw getEventSchema response before
        presenting contact attributes to users. The API returns every field ever observed
        on an (identify, user_properties) event, which includes infrastructure fields that
        are not meaningful for customers to configure journey conditions on.

        KEEP IN SYNC with _FILTERED_CONTACT_ATTRIBUTE_PREFIXES in:
          python/agent-service/src/agent_service/mcps/regal_copilot/event_schema/api_service.py

        The Python MCP tool (list-contact-attributes) mirrors this filter so that Copilot
        and the journeys UI show the same attribute set. If you add or remove entries here,
        update the Python list too. If the API ever moves this filtering server-side,
        both lists can be removed.

        This will exclude ALL CHILDREN of a key (prefix match).
    */
    const filterAttributes = [
        "context",
        "ref_id",
        "original_phone",
        "original_email",
        "created_at",
        "last_updated_by",
        "contact_from",
        "rv_static_segments",
        "rv_properties.do_not_trigger_journeys",
        "_rv_linked_profile_table_version",
        "_rv_linked_session_table_version",
        "_rv_migration_status",
        "last_session_id",
        "lead_score",
        "linked_profile_id",
        "linked_session_id",
        "linked_user_id",
        "opt_in.[].ip",
        "opt_in.[].name",
        "opt_in.[].source",
        "opt_in.[].subscribed",
        "opt_in.[].text",
        "opt_in.[].timestamp",
        "ref_id",
        "session_external_id",
        "session_id",
        "version",
        "address",
        "age",
        "birthday",
        "custom_entities",
        "updated_at",
        "_rv_should_send_profile_event_to_destinations",
        "opt_in",
    ];
    return filterAttributes.reduce((shouldFilter, attr) => d.fieldName.startsWith(attr) || shouldFilter, false);
}

function cleanContactAttributes(
    contactAttributes: Array<EventSchema>,
    attributesMapper: AttrMapperFn,
    formatToObject: boolean
) {
    const requiredOptions = [
        {
            fieldName: "contactPhone",
            fieldType: "string",
        },
        {
            fieldName: "externalId",
            fieldType: "string",
        },
        {
            fieldName: "email",
            fieldType: "string",
        },
        {
            fieldName: "firstName",
            fieldType: "string",
        },
        {
            fieldName: "lastName",
            fieldType: "string",
        },
    ];

    remove(contactAttributes, contactAttributesFilter);
    const contactAttributeValues = [...requiredOptions, ...(contactAttributes || [])]?.map((d: Record<string, any>) => [
        attributesMapper(d.fieldName, camelCase),
        {
            fieldType: d.fieldType,
            fieldId: d.fieldId,
        },
    ]);

    return formatToObject ? Object.fromEntries(contactAttributeValues) : contactAttributeValues;
}

type AttrMapperFn = (s: string, convertFn: (p: string) => string) => string;

type useIVRSplitContactDataProps = {
    attributesMapper?: AttrMapperFn;
    formatToObject?: boolean;
};

export function useContactAttributesEditableQuery({
    attributesMapper = convertTopLevelAttribute,
    formatToObject = true,
}: useIVRSplitContactDataProps) {
    const { loading, data } = useQuery<EditableEventSchemaReturnType>(GET_EDITABLE_FIELDS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
        variables: {
            eventType: "identify",
            eventName: "user_properties",
        },
    });

    const contactAttributes = useMemo<EventSchemaOptionEntry>(() => {
        if (!data) {
            return [];
        }
        const { getEditableEventSchema } = data;
        return cleanContactAttributes(getEditableEventSchema, attributesMapper, formatToObject);
    }, [data, attributesMapper, formatToObject]);

    return { loading, data, contactAttributes };
}

export function useContactAttributesFullListQuery({
    attributesMapper = convertTopLevelAttribute,
    formatToObject = true,
}: useIVRSplitContactDataProps) {
    const { loading, data } = useQuery<EventSchemaListReturnType>(GET_EVENT_FIELDS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",

        variables: {
            eventType: "identify",
            eventName: "user_properties",
        },
    });

    const contactAttributes = useMemo<EventSchemaOptionEntry>(() => {
        if (!data) {
            return [];
        }
        const { getEventSchema } = data;
        return getEventSchema ? cleanContactAttributes(getEventSchema, attributesMapper, formatToObject) : [];
    }, [data, attributesMapper, formatToObject]);

    return { loading, data, contactAttributes };
}
